// Copyright © 2015-2020 TrampleZone LLC All Rights Reserved

// Advertising Module

// This module provides support for advertising in the Client application.
// There are several different types of ads supported: banners, verticalbanners,
// smallsquares, squares, skyscrapers, leaderboards, and interstitials.  NOTE:
// There are 2 vertical banners as they are small and more than one can fit.
//
// Supported ad platforms include...
//
// Google AdMob:    Installed mobile applications
// Google AdSense:  Mobile and Desktop browsers
// Chitika:         Mobile and Desktop browsers
// RevenueHits;     Mobile and Desktop browsers
// Amazon Affiliate Mobile and Desktop browsers

// This module manages active ad platforms for both mobile and desktop.
// It directs requests to right active ad platform.  There is a module
// for each ad platform that handles credentials and showing/hiding ads.
//
// Support for throttling display rate is also provided.  For example,
// one can decide to only show an ad in the slot a certain percentage of the
// time.  This is important to be responsive to user input and maximize
// ad revenue and user satisfaction.


define('Advertising',['require','AdMobPlatform','AdSensePlatform','ChitikaPlatform','RevenueHitsPlatform','AmazonPlatform','Utilities'],function (require) {
    'use strict';

    var // Require.js module dependencies
        AdMobPlatform = require('AdMobPlatform'),
        AdSensePlatform = require('AdSensePlatform'),
        ChitikaPlatform = require('ChitikaPlatform'),
        RevenueHitsPlatform = require('RevenueHitsPlatform'),
        AmazonPlatform = require('AmazonPlatform'),
        Utilities = require('Utilities'),

        // Common
        utilities,

        // Instance
        main,
        footerDiv,
        iframeContainer = false,
        isMobileApp = false,
        initialized = false,
        platforms = {},
        defaultPlatformName,
        showAds;  // Global switch to show/notshow ads


    // Constructor
    function Advertising () {
        // Dependent resources
        utilities = new Utilities();

        // Instantiate ad platforms
        if (utilities.isMobile() && utilities.isMobileApp()) {
            // Switch to show/notshow *mobile* ads
            showAds = true;

            // Running on mobile
            isMobileApp = true;

            // Instantiate platform(s) for mobile
            defaultPlatformName = this.platformNames.ADMOB;
            platforms[this.platformNames.ADMOB] = new AdMobPlatform();
        } else {
            // Switch to show/notshow *desktop* ads
            showAds = true;

            // Determine if running in iframe child container
            try {
                if (window.self !== window.top) {
                    iframeContainer = true;
                }
            } catch (error) {
                iframeContainer = true;
            }

            // Instantiate platform(s) for desktop
            defaultPlatformName = this.platformNames.CHITIKA;
            platforms[this.platformNames.ADSENSE] = new AdSensePlatform();
            platforms[this.platformNames.CHITIKA] = new ChitikaPlatform();
            platforms[this.platformNames.REVENUE_HITS] = new RevenueHitsPlatform();
            platforms[this.platformNames.AMAZON] = new AmazonPlatform();
        }
    };

    // Ad Platforms
    Advertising.platformNames = Object.freeze({
        ADMOB: 'AdMob',
        ADSENSE: 'AdSense',
        CHITIKA: 'Chitika',
        REVENUE_HITS: 'RevenueHits',
        AMAZON: 'Amazon'
    });
    Advertising.prototype.platformNames = Advertising.platformNames;

    // Public Prototype Methods

    // Initializations
    Advertising.prototype.initialize = function (newMain) {
        // Capture main calling program
        main = newMain;
        // Initialize ad platforms (regardless of showAds)
        var platformNames = utilities.getObjectKeys(platforms);
        for (var i=0; i<platformNames.length; i++) {
            platforms[platformNames[i]].initialize(main);
        }
        // Get footer div in play
        footerDiv = main.getFooterDiv();
        // Set state to initialized
        initialized = true;
    };

    // Show banner ad (or leaderboard if desktop && wide enough)
    Advertising.prototype.showBannerAd = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            if (iframeContainer) {
                // Show no ad as the parent will handle
                if (callback) {
                    callback();
                }
            } else {
                // Show/Hide footer if on desktop
                if (!isMobileApp) {
                    footerDiv.style.display = (visible ? 'block' : 'none');
                }
                // Show/Hide banner ad
                platforms[name].showBannerAd(
                    visible, parentDiv, callback);
            }
        } else {
            // Do nothing and just callback
            if (callback) {
                callback();
            }
        }
    };

    // Show verticalbanner1 ad
    Advertising.prototype.showVerticalbanner1Ad = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            platforms[name].showVerticalbanner1Ad(
                visible, parentDiv, callback);
        }
    };

    // Show verticalbanner2 ad
    Advertising.prototype.showVerticalbanner2Ad = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            platforms[name].showVerticalbanner2Ad(
                visible, parentDiv, callback);
        }
    };

    // Show leaderboard ad
    Advertising.prototype.showLeaderboardAd = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            if (iframeContainer) {
                // Show no ad as the parent will handle
                if (callback) {
                    callback();
                }
            } else {
                // Show/Hide footer if on desktop
                if (!isMobileApp) {
                    footerDiv.style.display = (visible ? 'block' : 'none');
                }
                // Show/Hide leaderboard ad
                platforms[name].showLeaderboardAd(
                    visible, parentDiv, callback);
            }
        } else {
            // Do nothing and just callback
            if (callback) {
                callback();
            }
        }
    };

    // Show small square ad
    Advertising.prototype.showSmallSquareAd = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            platforms[name].showSmallSquareAd(
                visible, parentDiv, callback);
        }
    };

    // Show square ad
    Advertising.prototype.showSquareAd = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            platforms[name].showSquareAd(
                visible, parentDiv, callback);
        }
    };

    // Show skyscraper ad
    Advertising.prototype.showSkyscraperAd = function (
        visible, platformName, parentDiv, callback) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            platforms[name].showSkyscraperAd(
                visible, parentDiv, callback);
        }
    };

    // Download interstitial ad based on platform name
    Advertising.prototype.prepareInterstitialAd = function (platformName) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            // Only mobile for now
            if (isMobileApp) {
                platforms[name].prepareInterstitialAd();
            }
        }
    };

    // Show interstitial ad based on platform name
    Advertising.prototype.showInterstitialAd = function (platformName) {
        var name = platformName || defaultPlatformName;
        if (name && showAds) {
            // Only mobile for now
            if (isMobileApp) {
                platforms[name].showInterstitialAd();
            }
        }
    };

    // Get showAds
    Advertising.prototype.getShowAds = function () {
        return showAds;
    };

    // Set showAds
    Advertising.prototype.setShowAds = function (newShowAds) {
        showAds = newShowAds;
    };

    // Get initialize state
    Advertising.prototype.isInitialized = function () {
        return initialized;
    };

    // Return Constructor
    return Advertising.prototype.constructor;
});

